import { useCallback, useEffect, useState } from "react";
import Alert from "./Alert";
import AlertConfirmation from "./AlertConfirmation";
import AlertPrompt from "./AlertPrompt";
import TraitScaleConfirm from "./TraitScaleConfirm";
import { TGetAllTraitItem } from '../api/trait';
import FeedbackAlert, { TFeedbackData } from "./FeedbackAlert";
import AlertInfo from "./AlertInfo";

type TShowAlert = {
  title?: string|JSX.Element|JSX.Element[];
  description?: string|JSX.Element|JSX.Element[];
  labelOK?: string;
  onOK?: () => void;
  isCloseable?: boolean;
};

type TShowAlertConfirm = {
  title?: string|JSX.Element|JSX.Element[];
  description?: string|JSX.Element|JSX.Element[];
  labelYes?: string;
  labelNo?: string;
  onYes?: () => void;
  onNo?: () => void;
};

type TShowAlertPrompt = {
  title?: string|JSX.Element|JSX.Element[];
  description?: string|JSX.Element|JSX.Element[];
  input?: string;
  inputPlaceholder?: string;
  labelYes?: string;
  labelNo?: string;
  onYes?: (input: string) => void;
  onNo?: () => void;
};

type TShowTraitScaleConfirm = {
  title?: string|JSX.Element|JSX.Element[];
  description?: string|JSX.Element|JSX.Element[];
  labelYes?: string;
  labelNo?: string;
  onYes?: () => void;
  onNo?: () => void;
  readonly?: boolean;
};

type TShowChangeTraitGoal = {
  labelSubmit?: string;
  onSubmit?: () => void;
  trait: TGetAllTraitItem;
}

export type TModulePopup = {
  showAlert: (p: TShowAlert) => void;
  hideAlert: () => void;
  showAlertConfirm: (p: TShowAlertConfirm) => void;
  showAlertPrompt: (p: TShowAlertPrompt) => void;
  showTraitScaleConfirm: (p: TShowTraitScaleConfirm) => void;
  showChangeTraitGoal: (p: TShowChangeTraitGoal) => void;
  showFeedbackAlert: (p: TShowFeedbackAlert) => void;
  showAlertInfo: (p: TShowAlertInfo) => void;
  hideAlertInfo: () => void;
};

type TShowFeedbackAlert = {
  title?: string | JSX.Element | JSX.Element[];
  description?: string | JSX.Element | JSX.Element[];
  onSubmit?: (feedback: TFeedbackData) => void;
  onHome?: () => void;
  onClose?: () => void;
  buttonLabel?: string;
  defaultRating?: "like" | "dislike" | null;
};

type TShowAlertInfo = {
  title?: string | JSX.Element | JSX.Element[];
  description?: string | JSX.Element | JSX.Element[];
  size?: "sm" | "lg" | "xl";
  onClose?: () => void;
};

const ModulePopup: {[key: string]: any} = {};

export function ModulePopupContainer() {
  const [_showAlert, _setShowAlert] = useState(false);
  const [_showAlertInfo, _setShowAlertInfo] = useState<TShowAlert>();
  const showAlert = useCallback((p: TShowAlert) => {
    _setShowAlertInfo(p);
    _setShowAlert(true);
  }, []);
  const hideAlert = useCallback(() => {
    _setShowAlert(false);
  }, []);

  const [_showAlertConfirm, _setShowAlertConfirm] = useState(false);
  const [_showAlertConfirmInfo, _setShowAlertConfirmInfo] = useState<TShowAlertConfirm>();
  const showAlertConfirm = useCallback((p: TShowAlert) => {
    _setShowAlertConfirmInfo(p);
    _setShowAlertConfirm(true);
  }, []);

  const [_showAlertPrompt, _setShowAlertPrompt] = useState(false);
  const [_showAlertPromptInfo, _setShowAlertPromptInfo] = useState<TShowAlertPrompt>();
  const showAlertPrompt = useCallback((p: TShowAlertPrompt) => {
    _setShowAlertPromptInfo(p);
    _setShowAlertPrompt(true);
  }, []);

  const [_showTraitScaleConfirm, _setShowTraitScaleConfirm] = useState(false);
  const [_showTraitScaleConfirmInfo, _setTraitScaleConfirmInfo] = useState<TShowTraitScaleConfirm>();
  const showTraitScaleConfirm = useCallback((p: TShowAlert) => {
    _setTraitScaleConfirmInfo(p);
    _setShowTraitScaleConfirm(true);
  }, []);

  const [_showFeedbackAlert, _setShowFeedbackAlert] = useState(false);
  const [_showFeedbackAlertInfo, _setShowFeedbackAlertInfo] = useState<TShowFeedbackAlert>();
  const showFeedbackAlert = useCallback((p: TShowFeedbackAlert) => {
    _setShowFeedbackAlert(false);_setShowFeedbackAlertInfo(p);
    _setShowFeedbackAlert(true);
  }, []);

  const [_showAlertInfoData, _setShowAlertInfoData] = useState(false);
  const [_alertInfoData, _setAlertInfoData] = useState<TShowAlertInfo>();

  const showAlertInfo = useCallback((p: TShowAlertInfo) => {
    _setAlertInfoData(p);
    _setShowAlertInfoData(true);
  }, []);

  const hideAlertInfo = useCallback(() => {
    _setShowAlertInfoData(false);
  }, []);

  // registers the methods
  useEffect(() => {
    ModulePopup.showAlert = showAlert;
    ModulePopup.hideAlert = hideAlert;
    ModulePopup.showAlertConfirm = showAlertConfirm;
    ModulePopup.showAlertPrompt = showAlertPrompt;
    ModulePopup.showTraitScaleConfirm = showTraitScaleConfirm;
    ModulePopup.showFeedbackAlert = showFeedbackAlert;
    ModulePopup.showAlertInfo = showAlertInfo;
    ModulePopup.hideAlertInfo = hideAlertInfo;
  }, [showFeedbackAlert]);

  return <>
    <Alert show={_showAlert} setShow={_setShowAlert}
      onOK={() => {
        _setShowAlert(false);

        if (typeof _showAlertInfo?.onOK === "function") {
          _showAlertInfo?.onOK();
        }
      }}
      title={_showAlertInfo?.title}
      description={_showAlertInfo?.description}
      labelOK={_showAlertInfo?.labelOK}
      isCloseable={_showAlertInfo?.isCloseable}
    />

    <AlertConfirmation show={_showAlertConfirm} setShow={_setShowAlertConfirm}
      onNo={() => {
        _setShowAlertConfirm(false);

        if (typeof _showAlertConfirmInfo?.onNo === "function") {
          _showAlertConfirmInfo?.onNo();
        }
      }}
      onYes={() => {
        _setShowAlertConfirm(false);

        if (typeof _showAlertConfirmInfo?.onYes === "function") {
          _showAlertConfirmInfo?.onYes();
        }
      }}
      title={_showAlertConfirmInfo?.title}
      description={_showAlertConfirmInfo?.description}
      labelNo={_showAlertConfirmInfo?.labelNo}
      labelYes={_showAlertConfirmInfo?.labelYes}
    />

    <AlertPrompt show={_showAlertPrompt} setShow={_setShowAlertPrompt}
      onNo={() => {
        _setShowAlertPrompt(false);

        if (typeof _showAlertPromptInfo?.onNo === "function") {
          _showAlertPromptInfo?.onNo();
        }
      }}
      onYes={(words) => {
        _setShowAlertPrompt(false);

        if (typeof _showAlertPromptInfo?.onYes === "function") {
          _showAlertPromptInfo?.onYes(words);
        }
      }}
      input={_showAlertPromptInfo?.input}
      inputPlaceholder={_showAlertPromptInfo?.inputPlaceholder}
      title={_showAlertPromptInfo?.title}
      description={_showAlertPromptInfo?.description}
      labelNo={_showAlertPromptInfo?.labelNo}
      labelYes={_showAlertPromptInfo?.labelYes}
    />

    <TraitScaleConfirm show={_showTraitScaleConfirm} setShow={_setShowTraitScaleConfirm}
      readonly={_showTraitScaleConfirmInfo?.readonly ?? false}
      onNo={() => {
        _setShowTraitScaleConfirm(false);

        if (typeof _showTraitScaleConfirmInfo?.onNo === "function") {
          _showTraitScaleConfirmInfo?.onNo();
        }
      }}
      onYes={() => {
        _setShowTraitScaleConfirm(false);

        if (typeof _showTraitScaleConfirmInfo?.onYes === "function") {
          _showTraitScaleConfirmInfo?.onYes();
        }
      }}
      title={_showTraitScaleConfirmInfo?.title}
      description={_showTraitScaleConfirmInfo?.description}
      labelNo={_showTraitScaleConfirmInfo?.labelNo}
      labelYes={_showTraitScaleConfirmInfo?.labelYes}
    />

    <FeedbackAlert
      show={_showFeedbackAlert}
      setShow={_setShowFeedbackAlert}
      title={_showFeedbackAlertInfo?.title}
      description={_showFeedbackAlertInfo?.description}
      buttonLabel={_showFeedbackAlertInfo?.buttonLabel}
      defaultRating={_showFeedbackAlertInfo?.defaultRating}
      onSubmit={(feedback: TFeedbackData) => {
        if (typeof _showFeedbackAlertInfo?.onSubmit === "function") {
          _showFeedbackAlertInfo.onSubmit(feedback);
        }
      }}
      onHome={() => {
        if (typeof _showFeedbackAlertInfo?.onHome === "function") {
          _showFeedbackAlertInfo.onHome();
        }
      }}
      onClose={() => {
        _setShowFeedbackAlert(false);
        _setShowFeedbackAlertInfo(undefined);
        if (typeof _showFeedbackAlertInfo?.onClose === "function") {
          _showFeedbackAlertInfo.onClose();
        }
      }}
    />

    <AlertInfo
      show={_showAlertInfoData}
      setShow={_setShowAlertInfoData}
      title={_alertInfoData?.title}
      description={_alertInfoData?.description}
      size={_alertInfoData?.size}
      onClose={() => {
        _setShowAlertInfoData(false);
        _alertInfoData?.onClose?.();
      }}
    />
  </>;
}

export default ModulePopup as TModulePopup;
